import React, { useState } from "react";
import { useDispatch } from "react-redux";
import "./PlateViewer.scss";
import PlateWell from "../PlateWell/PlateWell";
import { WellPosition } from "../../Content";
import { onSetWellsType } from "../../../../store";

interface PlateViewerProps {
  title: string;
  wellsContent: Record<WellPosition, { [key: string]: number }>;
}

type WellStatus = "empty" | "filled";
type WellType = "blank" | "sample" | "none";

const PlateViewer = ({ title, wellsContent }: PlateViewerProps) => {
  const dispatch = useDispatch();
  const rows = ["A", "B", "C", "D", "E", "F", "G", "H"];
  const columns = Array.from({ length: 12 }, (_, i) => i + 1);

  const wellsStatus = useState<Record<string, WellStatus>>(() => {
    const initialState: Record<string, WellStatus> = {};
    Object.keys(wellsContent).forEach((well) => {
      initialState[well] = "filled";
    });
    return initialState;
  })[0];

  const [wellsType, setWellsType] = useState<Record<string, WellType>>(() => {
    const initialState: Record<string, WellType> = {};
    return initialState;
  });

  const [activeWellType, setActiveWellType] = useState<WellType>("blank");

  const handleColumnHeaderClick = (columnId: string) => {
    setWellsType((prev) => {
      const updatedWellsType = { ...prev };

      rows.forEach((row) => {
        const wellId = `${row}${columnId}`;
        const current = updatedWellsType[wellId] || "none";

        // Cycle through states: none -> activeWellType -> none
        const next: WellType =
          current === "none"
            ? activeWellType
            : current !== activeWellType
              ? activeWellType
              : "none";

        updatedWellsType[wellId] = next;
      });

      dispatch(onSetWellsType(updatedWellsType));
      return updatedWellsType;
    });
  };

  const handleWellClick = (id: string) => {
    setWellsType((prev) => {
      const current = prev[id] || "none";
      const next: WellType =
        current === "none"
          ? activeWellType
          : current !== activeWellType
            ? current === "blank"
              ? current
              : activeWellType
            : "none";
      const updatedWellsType = { ...prev, [id]: next };
      dispatch(onSetWellsType(updatedWellsType));
      return updatedWellsType;
    });
  };

  const handleWellTypeChange = (type: WellType) => {
    setActiveWellType(type);
  };

  return (
    <div className="plate-viewer">
      <h2 className="plate-title">{title}</h2>
      <div className="plate-viewer-content">
        <div className="plate-selector">
          <button
            className={`plate-type-button ${activeWellType === "blank" ? "active" : ""}`}
            onClick={() => handleWellTypeChange("blank")}
            aria-label="Select Blank Type"
          >
            <div className="circle-icon blank"></div>
            <span>Blank</span>
          </button>

          <button
            className={`plate-type-button ${activeWellType === "sample" ? "active" : ""}`}
            onClick={() => handleWellTypeChange("sample")}
            aria-label="Select Sample Type"
          >
            <div className="circle-icon sample"></div>
            <span>Sample</span>
          </button>
        </div>

        <div className="plate-grid" aria-label="Microplate Grid">
          <div className="plate-top-left-corner"></div>
          <div className="column-headers">
            <div className="spacer"></div>
            {columns.map((col) => (
              <button
                key={`col-${col}`}
                className="column-header"
                onClick={() => handleColumnHeaderClick(col.toString())}
                tabIndex={0}
              >
                {col}
              </button>
            ))}
          </div>

          <div className="plate-content">
            <div className="row-headers">
              {rows.map((row) => (
                <div key={`row-${row}`} className="row-header">
                  {row}
                </div>
              ))}
            </div>

            <div className="wells-grid">
              {rows.map((row) => (
                <div key={`row-${row}`} className="well-row">
                  {columns.map((col) => {
                    const wellId = `${row}${col}`;
                    const status = wellsStatus[wellId] || "empty";
                    const type = wellsType[wellId] || "none";
                    return (
                      <PlateWell
                        key={wellId}
                        id={wellId}
                        status={status}
                        type={type}
                        onClick={() => handleWellClick(wellId)}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          <div className="plate-bottom-left-corner"></div>
        </div>

        <div className="plate-legend" aria-label="Microplate keys">
          <div className="legend-title">Key:</div>
          <div className="legend-item" aria-label="Blank Well key">
            <div className="circle-icon empty"></div>
            <span>Empty</span>
          </div>
          <div className="legend-item" aria-label="Filled Well key">
            <div className="circle-icon filled"></div>
            <span>Filled</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlateViewer;
